/**
 * Custom Block Loader
 *
 * Fetches and registers custom blocks for a workspace.
 * Called during widget initialization before rendering begins.
 */

import type { CustomBlockConfig } from '../ui/blocks/custom-templates';
import { registerCustomBlocks } from '../ui/blocks/custom-templates';

// Cache loaded blocks per workspace to avoid refetching
const loadedWorkspaces = new Set<string>();

/**
 * Load and register custom blocks for a workspace.
 *
 * @param apiUrl - Base API URL
 * @param workspaceId - Workspace ID
 * @returns Promise that resolves when blocks are loaded
 */
export async function loadCustomBlocks(apiUrl: string, workspaceId: string): Promise<void> {
  // Skip if already loaded for this workspace
  if (loadedWorkspaces.has(workspaceId)) {
    return;
  }

  try {
    const response = await fetch(`${apiUrl}/api/workspaces/${workspaceId}/custom-blocks`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      // Not an error - workspace might not have custom blocks
      if (response.status === 404) {
        console.debug('[CustomBlockLoader] No custom blocks for workspace');
        loadedWorkspaces.add(workspaceId);
        return;
      }
      throw new Error(`Failed to load custom blocks: ${response.status}`);
    }

    const blocks: CustomBlockConfig[] = await response.json();

    if (blocks.length > 0) {
      registerCustomBlocks(blocks);
      console.debug(`[CustomBlockLoader] Loaded ${blocks.length} custom blocks for workspace ${workspaceId}`);
    }

    loadedWorkspaces.add(workspaceId);
  } catch (error) {
    // Log but don't fail - custom blocks are optional
    console.warn('[CustomBlockLoader] Failed to load custom blocks:', error);
    loadedWorkspaces.add(workspaceId); // Mark as loaded to avoid retries
  }
}

/**
 * Clear the loaded workspaces cache.
 * Useful for testing or when switching workspaces.
 */
export function clearCustomBlockCache(): void {
  loadedWorkspaces.clear();
}
